import type { ClipboardEvent, KeyboardEvent, Ref } from 'react';
import { forwardRef, useMemo, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

type OtpInputProps = {
  /** The joined digit string (source of truth). */
  value?: string;
  /** Called with the joined digit string whenever a box changes. */
  onChange?: (value: string) => void;
  onBlur?: () => void;
  /** Number of boxes / digits. Defaults to 6. */
  length?: number;
  error?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  autoFocus?: boolean;
  'aria-label'?: string;
};

/**
 * Segmented one-time-code input: `length` single-digit boxes that together read
 * and write one joined digit string. Fully controlled — `value` is the source of
 * truth and `onChange` receives the new joined string. Supports typing with
 * auto-advance, Backspace/Arrow navigation, and pasting the whole code into any
 * box. The first box carries autoComplete="one-time-code" so browser/OS code
 * autofill lands here. Numeric only, so leading zeros are preserved as text.
 */
function OtpInput(
  {
    value = '',
    onChange,
    onBlur,
    length = 6,
    error,
    disabled,
    id,
    name,
    autoFocus,
    'aria-label': ariaLabel,
  }: OtpInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  const digits = useMemo(() => {
    const clean = value.replace(/\D/g, '').slice(0, length).split('');
    return Array.from({ length }, (_, i) => clean[i] ?? '');
  }, [value, length]);

  const commit = (next: string[]) => onChange?.(next.join(''));

  const focusBox = (i: number) => {
    const el = inputs.current[Math.max(0, Math.min(length - 1, i))];
    el?.focus();
    el?.select();
  };

  const handleChange = (i: number, raw: string) => {
    const clean = raw.replace(/\D/g, '');

    // Paste or autofill dumped multiple digits into one box: fill from the start.
    if (clean.length > 1) {
      const next = Array.from({ length }, (_, k) => clean[k] ?? '');
      commit(next);
      focusBox(Math.min(clean.length, length - 1));
      return;
    }

    const next = [...digits];
    next[i] = clean; // '' clears, a single digit sets
    commit(next);
    if (clean) {
      focusBox(i + 1);
    }
  };

  const handleKeyDown = (i: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const next = [...digits];
      if (digits[i]) {
        next[i] = '';
        commit(next);
      } else if (i > 0) {
        next[i - 1] = '';
        commit(next);
        focusBox(i - 1);
      }
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(i - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(i + 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) {
      return;
    }
    event.preventDefault();
    const next = Array.from({ length }, (_, k) => pasted[k] ?? '');
    commit(next);
    focusBox(Math.min(pasted.length, length - 1));
  };

  return (
    <div className="flex items-center justify-between gap-2 sm:gap-3">
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            inputs.current[i] = el;
            if (i === 0) {
              if (typeof ref === 'function') {
                ref(el);
              } else if (ref) {
                (ref as { current: HTMLInputElement | null }).current = el;
              }
            }
          }}
          id={i === 0 ? id : undefined}
          name={i === 0 ? name : undefined}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          pattern="[0-9]*"
          // No maxLength: some phones autofill the whole code into one box; the
          // controlled value keeps each box at one digit and handleChange spreads
          // an autofilled/pasted code across the boxes.
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={ariaLabel ? `${ariaLabel} digit ${i + 1}` : `Digit ${i + 1}`}
          value={digit}
          onChange={(event) => handleChange(i, event.target.value)}
          onKeyDown={(event) => handleKeyDown(i, event)}
          onPaste={handlePaste}
          onFocus={(event) => event.target.select()}
          onBlur={onBlur}
          className={twMerge(
            'h-12 w-full min-w-0 rounded-md border-0 text-center font-semibold text-lg text-zinc-900 tabular-nums shadow-xs ring-1 ring-zinc-300 ring-inset focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-inset focus-visible:outline-hidden dark:bg-white/5 dark:text-zinc-100 dark:ring-white/10 dark:focus:ring-emerald-400',
            error && 'ring-red-500 dark:ring-red-500',
          )}
        />
      ))}
    </div>
  );
}

export default forwardRef(OtpInput);
